import { Flex, TextButton } from "@amzn/storm-ui";
import type { AsyncStatus } from "@react-hookz/web";
import React from "react";
import { useStore } from "zustand";

import { getInsightsFromAIFilters } from "../../../config/filters/insightsFromAI";
import { discoveryFilterStore } from "../../../store/filters";
import type { Item } from "../../../types/common";
import type {
    DiscoveryFilter,
    DiscoveryFilterField,
} from "../../../types/filters/common";
import { InsightsFromAIFilterField } from "../../../types/filters/insightFromAI";
import { isLoading } from "../../../utils/api.utils";
import { isAccountAgnosticId } from "../../../utils/permission.utils";

import FilterButton from "./FilterButton";
import { FilterContainer } from "./discoveryFilterSharedStyles";

export const ExploreWithAIFilters: React.FC<{
    setIsInitialLoad: (e: boolean) => void;
    filterStatus?: AsyncStatus;
}> = ({ setIsInitialLoad, filterStatus }) => {
    const [contentSubType, filters, hasAppliedFilters, setField, resetFilters] =
        useStore(discoveryFilterStore, (state) => [
            state.getContentSubType(),
            state.getFilters(),
            state.hasAppliedFilters,
            state.setField,
            state.resetFilters,
        ]);

    const { fieldSelections, filterOptions } = filters[contentSubType.provider];

    const handleRemoveAllFilters = (): void => {
        resetFilters();
    };

    const isMultiSelect = (field: DiscoveryFilterField) =>
        field === InsightsFromAIFilterField.CONTENT_TYPES;

    const handleOptionClick = (field: DiscoveryFilterField, option: Item) => {
        setIsInitialLoad(false);
        setField(field, option, false, isMultiSelect(field));
    };

    const isAccountAgnostic = isAccountAgnosticId();

    return (
        <FilterContainer data-testid="explore-with-ai-filter">
            <Flex
                alignItems="center"
                rowGap="xsmall"
                columnGap="xsmall"
                wrap="wrap"
            >
                {getInsightsFromAIFilters(isAccountAgnostic).map(
                    (filter: DiscoveryFilter) => {
                        return (
                            <FilterButton
                                key={filter.field}
                                {...{
                                    filter,
                                    fieldSelections:
                                        fieldSelections[filter.field],
                                    options: filterOptions[filter.field],
                                    onOptionClick: (option: Item) => {
                                        handleOptionClick(filter.field, option);
                                    },
                                    singleSelect: !isMultiSelect(filter.field),
                                }}
                                loading={
                                    !isAccountAgnostic &&
                                    filterStatus &&
                                    isLoading(filterStatus)
                                }
                            />
                        );
                    },
                )}
                <TextButton
                    onClick={handleRemoveAllFilters}
                    disabled={!hasAppliedFilters()}
                    data-testid="remove-all-filters-button"
                >
                    Clear added filters
                </TextButton>
            </Flex>
        </FilterContainer>
    );
};
